import { useState } from 'react';
import { X, Edit2, Save, Trash2 } from 'lucide-react';

function NoteModal({ client, onClose, onSave }) {
  const [editingInternal, setEditingInternal] = useState(false);
  const [editingExternal, setEditingExternal] = useState(false);
  const [internalNote, setInternalNote] = useState(client.internal_note || '');
  const [externalNote, setExternalNote] = useState(client.external_note || '');
  const [showClearConfirm, setShowClearConfirm] = useState(null); // 'internal' or 'external'

  function handleSaveInternal() {
    onSave({ ...client, internal_note: internalNote });
    setEditingInternal(false);
  }

  function handleSaveExternal() {
    onSave({ ...client, external_note: externalNote });
    setEditingExternal(false);
  }

  function handleClearNote(noteType) {
    if (noteType === 'internal') {
      onSave({ ...client, internal_note: '' });
      setInternalNote('');
      setShowClearConfirm(null);
    } else {
      onSave({ ...client, external_note: '' });
      setExternalNote('');
      setShowClearConfirm(null);
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--surface)',
        borderRadius: '0.5rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border)'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
              Notes for {client.first_name} {client.last_name}
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-light)' }}>
              View and manage client notes
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-light)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Internal Note */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.75rem'
            }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                Internal Note
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {!editingInternal ? (
                  <>
                    <button
                      onClick={() => setEditingInternal(true)}
                      className="btn btn-secondary"
                      style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    {internalNote && (
                      <button
                        onClick={() => setShowClearConfirm('internal')}
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                      >
                        <Trash2 size={14} />
                        Clear
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleSaveInternal}
                    className="btn btn-primary"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    <Save size={14} />
                    Save
                  </button>
                )}
              </div>
            </div>

            {showClearConfirm === 'internal' ? (
              <div style={{
                padding: '1rem',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '0.375rem',
                marginBottom: '0.5rem'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#92400e' }}>
                  Are you sure you want to clear this note? This action cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleClearNote('internal')}
                    className="btn btn-danger"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    Yes, Clear Note
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(null)}
                    className="btn btn-secondary"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {editingInternal ? (
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter internal note (staff only)..."
              />
            ) : (
              <div style={{
                padding: '0.75rem',
                background: 'var(--background)',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                minHeight: '4rem',
                color: internalNote ? 'var(--text)' : 'var(--text-light)',
                whiteSpace: 'pre-wrap'
              }}>
                {internalNote || 'No internal note'}
              </div>
            )}
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.75rem',
              color: 'var(--text-light)'
            }}>
              For internal staff use only
            </p>
          </div>

          {/* External Note */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.75rem'
            }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                External Note
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {!editingExternal ? (
                  <>
                    <button
                      onClick={() => setEditingExternal(true)}
                      className="btn btn-secondary"
                      style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    {externalNote && (
                      <button
                        onClick={() => setShowClearConfirm('external')}
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                      >
                        <Trash2 size={14} />
                        Clear
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleSaveExternal}
                    className="btn btn-primary"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    <Save size={14} />
                    Save
                  </button>
                )}
              </div>
            </div>

            {showClearConfirm === 'external' ? (
              <div style={{
                padding: '1rem',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '0.375rem',
                marginBottom: '0.5rem'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#92400e' }}>
                  Are you sure you want to clear this note? This action cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleClearNote('external')}
                    className="btn btn-danger"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    Yes, Clear Note
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(null)}
                    className="btn btn-secondary"
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {editingExternal ? (
              <textarea
                value={externalNote}
                onChange={(e) => setExternalNote(e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter external note (may be sent to client)..."
              />
            ) : (
              <div style={{
                padding: '0.75rem',
                background: 'var(--background)',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                minHeight: '4rem',
                color: externalNote ? 'var(--text)' : 'var(--text-light)',
                whiteSpace: 'pre-wrap'
              }}>
                {externalNote || 'No external note'}
              </div>
            )}
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.75rem',
              color: 'var(--text-light)'
            }}>
              May be included in SMS notifications to client
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default NoteModal;
